import React from 'react';
import { Handle, Position } from 'reactflow';
import { truncateText, getStatusColor } from '../lib/flowchartHelpers';
import { Plus } from 'lucide-react';

export function StageNode({ data }) {
  const handleAddVariant = (e) => {
    e.stopPropagation();
    if (data.onAddVariant) {
      data.onAddVariant(data.stageIndex);
    }
  };

  return (
    <div className="bg-white border-2 border-blue-500 rounded-lg shadow-lg p-4 min-w-[280px] max-w-[320px] group hover:shadow-xl transition-shadow">
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-blue-500" />
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">
            ЭТАП {data.stageNumber}
          </span>
          {data.variants > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {data.variants} вар.
            </span>
          )}
        </div>
        
        <h3 className="font-bold text-sm text-gray-900 leading-tight">
          {truncateText(data.label, 60)}
        </h3>
        
        {data.description && (
          <p className="text-xs text-gray-600 leading-snug">
            {truncateText(data.description, 80)}
          </p>
        )}
        
        {data.skipIf && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-xs text-orange-600 italic">
              <span className="font-semibold">Пропустить если:</span> {truncateText(data.skipIf, 60)}
            </p>
          </div>
        )}
        
        <button
          onClick={handleAddVariant}
          className="w-full mt-2 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors opacity-0 group-hover:opacity-100"
          title="Добавить вариант"
        >
          <Plus size={14} />
          Добавить вариант
        </button>
      </div>
      
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-blue-500" />
    </div>
  );
}

export function StepNode({ data }) {
  const handleAddStepVar = (e) => {
    e.stopPropagation();
    if (data.onAddStepVar) {
      data.onAddStepVar(data.stageIndex, data.variantIndex, data.stepIndex);
    }
  };

  return (
    <div className="bg-white border-2 border-purple-500 rounded-lg shadow-md p-3 min-w-[240px] max-w-[280px] group hover:shadow-lg transition-shadow">
      <Handle type="target" position={Position.Left} className="w-3 h-3 bg-purple-500" />
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded">
            ШАГ {data.stepNumber}
          </span>
          {data.vars > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {data.vars} вар.
            </span>
          )}
        </div>
        
        {data.variantName && (
          <div className="text-xs text-purple-600 font-medium">
            {truncateText(data.variantName, 40)}
          </div>
        )}
        
        <h4 className="font-semibold text-sm text-gray-900 leading-tight">
          {truncateText(data.label, 50)}
        </h4>
        
        {data.description && (
          <p className="text-xs text-gray-600 leading-snug">
            {truncateText(data.description, 70)}
          </p>
        )}
        
        {data.skipIf && (
          <div className="mt-1 pt-1 border-t border-gray-200">
            <p className="text-xs text-orange-600 italic">
              {truncateText(data.skipIf, 50)}
            </p>
          </div>
        )}
        
        <button
          onClick={handleAddStepVar}
          className="w-full mt-2 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded transition-colors opacity-0 group-hover:opacity-100"
          title="Добавить вариант шага"
        >
          <Plus size={14} />
          Добавить вариант
        </button>
      </div>
      
      <Handle type="source" position={Position.Right} className="w-3 h-3 bg-purple-500" />
    </div>
  );
}

export function StepVarNode({ data }) {
  const statusColor = getStatusColor(data.status);
  
  return (
    <div 
      className="bg-white border-2 rounded-lg shadow-sm p-3 min-w-[220px] max-w-[260px]"
      style={{ borderColor: statusColor }}
    >
      <Handle type="target" position={Position.Left} className="w-2 h-2" style={{ background: statusColor }} />
      
      <div className="space-y-2">
        {data.status && (
          <div 
            className="text-xs font-semibold text-white px-2 py-1 rounded text-center"
            style={{ backgroundColor: statusColor }}
          >
            {data.status}
          </div>
        )}
        
        <h5 className="font-semibold text-xs text-gray-900 leading-tight">
          {truncateText(data.label, 45)}
        </h5>
        
        {data.condition && (
          <div className="bg-yellow-50 border-l-2 border-yellow-400 px-2 py-1">
            <p className="text-xs text-yellow-800 italic">
              <span className="font-semibold">Условие:</span> {truncateText(data.condition, 50)}
            </p>
          </div>
        )}
        
        {data.task && (
          <div className="bg-blue-50 px-2 py-1 rounded">
            <p className="text-xs text-blue-900">
              <span className="font-semibold">Задача:</span> {truncateText(data.task, 60)}
            </p>
          </div>
        )}
        
        {data.approvedPhrases && (
          <div className="bg-green-50 px-2 py-1 rounded">
            <p className="text-xs text-green-900">
              <span className="font-semibold">Фразы:</span> {truncateText(data.approvedPhrases, 50)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export const nodeTypes = {
  stageNode: StageNode,
  stepNode: StepNode,
  stepVarNode: StepVarNode,
};
